import type {
  BankAccountEntry,
  BaseEntry,
  BoardEntry,
  BusinessPermitEntry,
  CertificationEntry,
  CompetencyEntry,
  ContactPersonRef,
  DeedEntry,
  EmailRef,
  EntryType,
  ExpertEntry,
  FileRef,
  FinancialStatementEntry,
  GeneralEntry,
  NumberRef,
  OfficeAddress,
  OfficeEntry,
  PicEntry,
  PortfolioEntry,
  PostalAddressRef,
  RawEntry,
  Ref,
  ShareholderEntry,
  TaxDocEntry,
  ToolEntry,
} from '../models/panel-entry.model';
import {
  epochNum,
  identOf,
  num,
  parseArr,
  parseBool,
  parseObj,
  str,
  strOrNum,
} from './panel-normalize.util';

/**
 * Normalizer per panel: satu baris MENTAH dari BE → entri bersih siap divalidasi.
 *
 * Dipakai untuk `items` DAN `revision_items` — bentuk keduanya identik (KONSEP §2.1).
 *
 * Akses field pakai bracket (`r['full_name']`), bukan titik: `RawEntry` adalah index signature
 * dan `noPropertyAccessFromIndexSignature` menyala di tsconfig.
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §3.3.
 */

const base = (r: RawEntry): BaseEntry => ({
  id: str(r['id']),
  identUuid: str(r['ident_uuid']),
  entryType: (str(r['entry_type']) ?? 'CREATE') as EntryType,
  approvedAt: epochNum(r['approved_at']),
  rejectedAt: epochNum(r['rejected_at']),
  comment: str(r['comment']),
});

export const normalizeOffice = (r: RawEntry): OfficeEntry => ({
  ...base(r),
  isPrimary: parseBool(r['is_primary']),
  officeType: parseObj<Ref>(r['office_type']),
  companyCategory: parseObj<Ref>(r['company_category']),
  businessEntity: parseObj<Ref>(r['business_entity']),
  fullName: str(r['full_name']),
  address: parseObj<OfficeAddress>(r['address']),
  phoneNumber: parseObj<NumberRef>(r['phone_number']),
  faxNumber: parseObj<NumberRef>(r['fax_number']),
  website: parseObj<EmailRef>(r['website']),
  email: parseObj<EmailRef>(r['email']),
});

/**
 * `profile_generals`. `legal_org_type` DAN `attachment` sama-sama dikirim endpoint LIST sebagai
 * string JSON — keduanya lewat `parseObj`. Response DETAIL (`GET :id`) mengirim objek betulan dan
 * TIDAK boleh dinormalisasi di sini (lihat `models/profile-general.model.ts`).
 */
export const normalizeGeneral = (r: RawEntry): GeneralEntry => ({
  ...base(r),
  fullName: str(r['full_name']),
  legalOrgType: parseObj<Ref>(r['legal_org_type']),
  attachment: parseObj<FileRef>(r['attachment']),
});

/**
 * `profile_deeds`. `deed_type` diparse DUA KALI dengan sengaja — `identOf` untuk validator badge
 * kelengkapan, `parseObj` untuk tampilan. Menyimpan hasilnya di variabel lokal menghemat
 * mikrodetik dan menukar dua baris sejajar dengan satu baris kondisional; daftar ini jauh lebih
 * sering dibaca daripada dijalankan.
 */
export const normalizeDeed = (r: RawEntry): DeedEntry => ({
  ...base(r),
  deedTypeIdent: identOf(r['deed_type']),
  deedType: parseObj<Ref>(r['deed_type']),
  files: parseArr<FileRef>(r['files']),
  number: str(r['number']),
  issuedDate: epochNum(r['issued_date']),
  notaryFullName: str(r['notary_full_name']),
  ackLetterNumber: str(r['ack_letter_number']),
  ackLetterDate: epochNum(r['ack_letter_date']),
});

/**
 * `profile_shareholders`. `nationality` diparse DUA KALI dengan sengaja — `identOf` untuk predikat
 * baris KTP/KITAS, `parseObj` untuk tampilan. Pola & alasan yang sama dengan `normalizeDeed`.
 *
 * `share_percentage` lewat `num()`, bukan `str()`: endpoint LIST mengirim STRING (`"0.22"`) dan
 * endpoint DETAIL mengirim number, sedangkan section menjumlahkannya untuk menghitung sisa kuota
 * persentase. `num()` menerima kedua bentuk.
 */
export const normalizeShareholder = (r: RawEntry): ShareholderEntry => ({
  ...base(r),
  fullName: str(r['full_name']),
  nationalityIdent: identOf(r['nationality']),
  nationality: parseObj<Ref>(r['nationality']),
  sharePercentage: num(r['share_percentage']),
  email: parseObj<EmailRef>(r['email']),
  ktpNumber: parseObj<NumberRef>(r['ktp'])?.number ?? null,
  ktpFile: parseObj<FileRef>(r['ktp_file']),
  kitasNumber: parseObj<NumberRef>(r['kitas'])?.number ?? null,
  kitasFile: parseObj<FileRef>(r['kitas_file']),
  // `npwp` & `npwp_file` SENGAJA tidak dinormalisasi: panel ini tidak menampilkan maupun
  // mengirimnya (keputusan user 31 Agu 2026). Di aplikasi acuan pun kedua templatnya kosong
  // (`//- decorate on pindad and eximbank`) — field itu milik tenant lain.
});

/**
 * `profile_bod_bocs`. `board_membership_type` dan `nationality` masing-masing diparse DUA KALI
 * dengan sengaja — `identOf` untuk validator badge kelengkapan & predikat baris KTP/KITAS,
 * `parseObj` untuk tampilan. Pola & alasan yang sama dengan `normalizeDeed` dan
 * `normalizeShareholder`.
 *
 * `signature_sample_file`, `npwp`, dan `npwp_file` SENGAJA tidak dinormalisasi: panel ini tidak
 * menampilkan maupun mengirimnya (keputusan user 31 Agustus 2026, PLAN-BOARDS D12).
 */
export const normalizeBoard = (r: RawEntry): BoardEntry => ({
  ...base(r),
  isCompanyHead: parseBool(r['is_company_head']),
  isShareholder: parseBool(r['is_shareholder']),
  boardTypeIdent: identOf(r['board_membership_type']),
  boardType: parseObj<Ref>(r['board_membership_type']),
  nationalityIdent: identOf(r['nationality']),
  nationality: parseObj<Ref>(r['nationality']),
  ktpNumber: parseObj<NumberRef>(r['ktp'])?.number ?? null,
  ktpFile: parseObj<FileRef>(r['ktp_file']),
  kitasNumber: parseObj<NumberRef>(r['kitas'])?.number ?? null,
  kitasFile: parseObj<FileRef>(r['kitas_file']),
  fullName: str(r['full_name']),
  positionTitle: str(r['position_title']),
  email: parseObj<EmailRef>(r['email']),
  phoneNumber: parseObj<NumberRef>(r['phone_number']),
});

/**
 * `profile_business_permits`. `legal_org_cred_type` dan `business_class` masing-masing diparse DUA
 * KALI dengan sengaja — `identOf` untuk kode mesin (validator badge kelengkapan & predikat baris
 * "Deskripsi Izin Usaha Lainnya"), `parseObj` untuk tampilan. Pola & alasan yang sama dengan
 * `normalizeDeed`, `normalizeShareholder`, dan `normalizeBoard`.
 *
 * `business_class` bisa datang tanpa `ident_name` — item `business_service_category` ("K1"…"P")
 * hanya `{ id, name }`. `identOf` mengembalikan `null` di situ, dan itu benar: golongan usaha jenis
 * itu memang tidak punya kode mesin.
 */
export const normalizeBusinessPermit = (r: RawEntry): BusinessPermitEntry => ({
  ...base(r),
  credTypeIdent: identOf(r['legal_org_cred_type']),
  credType: parseObj<Ref>(r['legal_org_cred_type']),
  otherCredType: str(r['other_legal_org_cred_type']),
  businessClassIdent: identOf(r['business_class']),
  businessClass: parseObj<Ref>(r['business_class']),
  docFile: parseObj<FileRef>(r['doc_file']),
  number: str(r['number']),
  fromDate: epochNum(r['from_date']),
  thruDate: epochNum(r['thru_date']),
  issuedBy: str(r['issued_by']),
});

export const normalizePic = (r: RawEntry): PicEntry => ({
  ...base(r),
  isPrimary: parseBool(r['is_primary']),
  username: str(r['username']),
  fullName: str(r['full_name']),
  mobileNumber: parseObj<NumberRef>(r['mobile_number']),
  email: parseObj<EmailRef>(r['email']),
});

/**
 * `profile_tools`. `tool_type` dikirim endpoint LIST sebagai string JSON — lewat `parseObj`.
 *
 * Response DETAIL (`GET :id`) mengirim objek betulan dan **TIDAK boleh** dinormalisasi di sini:
 * hasilnya bukan error, hanya objek bernama camelCase yang berbeda, dan modalnya terisi setengah
 * (lihat kepala `sections/tools/models/profile-tool.model.ts`).
 */
export const normalizeTool = (r: RawEntry): ToolEntry => ({
  ...base(r),
  toolType: parseObj<Ref>(r['tool_type']),
  // `strOrNum`, BUKAN `str`: endpoint DETAIL mengirim `qty` sebagai number, dan `str()`
  // membuangnya jadi `null`. Bukan `num()` juga — nilainya boleh berpemisah (`"1.000,50"`).
  qty: strOrNum(r['qty']),
  measurement: str(r['measurement']),
  brand: str(r['brand']),
  condition: str(r['condition']),
  location: str(r['location']),
  ownership: str(r['ownership']),
});

/**
 * `profile_experts`. `ktp` DAN `address` sama-sama dikirim endpoint LIST sebagai string JSON —
 * keduanya lewat `parseObj`.
 *
 * Response DETAIL (`GET :id`) mengirim objek betulan dan **TIDAK boleh** dinormalisasi di sini:
 * hasilnya bukan error, hanya objek bernama camelCase yang berbeda, dan modalnya terisi setengah
 * (lihat kepala `sections/experts/models/profile-expert.model.ts`).
 */
export const normalizeExpert = (r: RawEntry): ExpertEntry => ({
  ...base(r),
  fullName: str(r['full_name']),
  // Epoch DETIK. `epochNum()` menerima number, string numerik, maupun string ISO — BE mengirim
  // bentuk yang berbeda-beda tergantung endpoint.
  birthdate: epochNum(r['birthdate']),
  ktp: parseObj<NumberRef>(r['ktp']),
  address: parseObj<PostalAddressRef>(r['address']),
  lastEducation: str(r['last_education']),
  lastEducationMajor: str(r['last_education_major']),
  certification: str(r['certification']),
  certificationNumber: str(r['certification_number']),
  jobExperience: str(r['job_experience']),
  // `strOrNum`, BUKAN `str`: endpoint DETAIL mengirim `years_experience` sebagai number, dan
  // `str()` membuangnya jadi `null`. Bukan `num()` juga — nilainya ditampilkan apa adanya.
  yearsExperience: strOrNum(r['years_experience']),
});

/**
 * `profile_certifications`. `certification_type` DAN `doc_file` sama-sama dikirim endpoint LIST
 * sebagai string JSON — keduanya lewat `parseObj`.
 *
 * Response DETAIL (`GET :id`) mengirim keduanya sebagai objek betulan dan **TIDAK boleh**
 * dinormalisasi di sini: hasilnya bukan error, hanya objek bernama camelCase yang berbeda, dan
 * modalnya terisi setengah (lihat kepala
 * `sections/certifications/models/profile-certification.model.ts`).
 */
export const normalizeCertification = (r: RawEntry): CertificationEntry => ({
  ...base(r),
  certificationType: parseObj<Ref>(r['certification_type']),
  desc: str(r['desc']),
  // Epoch DETIK, field TANGGAL-SAJA. `epochNum()` menerima number, string numerik, maupun string
  // ISO — BE tidak konsisten antar endpoint. 🚫 Jangan `str()`: nilai number-nya dibuang jadi `null`.
  fromDate: epochNum(r['from_date']),
  thruDate: epochNum(r['thru_date']),
  docFile: parseObj<FileRef>(r['doc_file']),
});

export const normalizeCompetency = (r: RawEntry): CompetencyEntry => ({
  ...base(r),
  businessClassification: parseObj<Ref>(r['business_classification']),
  businessSubClassification: parseObj<Ref>(r['business_sub_classification']),
  supplyRole: parseObj<Ref>(r['supply_role']),
  files: parseArr<FileRef>(r['files']),
  desc: str(r['desc']),
});

/**
 * `profile_portfolios`. TUJUH field bersarang dikirim endpoint LIST sebagai STRING JSON — semuanya
 * lewat `parseObj`. Response DETAIL (`GET :id`) mengirim objek betulan dan TIDAK boleh
 * dinormalisasi di sini (lihat `sections/portfolios/models/profile-portfolio.model.ts`).
 *
 * `contract_value` lewat `num()`: LIST mengirim STRING `"15845450000.0"`, DETAIL mengirim number.
 * 🚫 Jangan `str()` (membuang yang number jadi `null`) dan jangan `strOrNum()` (mengembalikan
 * string, sedangkan barisnya butuh angka untuk diformat pemisah ribuan).
 *
 * `files` dan `contract_state` SENGAJA tidak dinormalisasi — keduanya tidak punya satu pun pembaca
 * (PLAN-PORTFOLIOS.md D4 & D18).
 *
 * ⚠️ Node di dalam `address` TIDAK di-`parseObj` lagi per node: di dalam string JSON `address`
 * tidak ada string JSON bersarang, jadi `country`/`province`/`district`/`sub_district` sudah objek
 * betulan begitu `address` diparse sekali.
 */
export const normalizePortfolio = (r: RawEntry): PortfolioEntry => ({
  ...base(r),
  businessClassification: parseObj<Ref>(r['business_classification']),
  businessSubClassification: parseObj<Ref>(r['business_sub_classification']),
  contractName: str(r['contract_name']),
  contractLocation: str(r['contract_location']),
  contractOwner: str(r['contract_owner']),
  contractNumber: str(r['contract_number']),
  address: parseObj<OfficeAddress>(r['address']),
  contactPerson: parseObj<ContactPersonRef>(r['contact_person']),
  fromDate: epochNum(r['from_date']),
  thruDate: epochNum(r['thru_date']),
  currency: parseObj<Ref>(r['currency']),
  contractValue: num(r['contract_value']),
  contractHandoverLetterNumber: str(r['contract_handover_letter_number']),
  contractHandoverLetterDate: epochNum(r['contract_handover_letter_date']),
  contractHandoverLetterFile: parseObj<FileRef>(r['contract_handover_letter_file']),
  testimony: str(r['testimony']),
  testimonyFile: parseObj<FileRef>(r['testimony_file']),
});

/**
 * `profile_bank_accounts` — panel 12 "Akun Bank".
 *
 * `accountNumber` pakai `str()`, **bukan `strOrNum()` apalagi `num()`**: nomor rekening adalah
 * TEKS. Nol di depan bermakna ("020601007329304") dan polanya mengizinkan huruf, strip, dan spasi;
 * `num()` di sini membuang nol depannya tanpa satu pun error.
 *
 * Tidak ada `num()` sama sekali di panel ini — ia tidak punya field tanggal yang ditampilkan.
 * `branch_name` sengaja tidak dinormalisasi: tidak ada pembacanya (PLAN-BANK-ACCOUNTS.md D9).
 */
export const normalizeBankAccount = (r: RawEntry): BankAccountEntry => ({
  ...base(r),
  bank: parseObj<Ref>(r['bank']),
  accountNumber: str(r['account_number']),
  accountHolderName: str(r['account_holder_name']),
  currency: parseObj<Ref>(r['currency']),
  address: parseObj<PostalAddressRef>(r['address']),
  bankStatementFile: parseObj<FileRef>(r['bank_statement_file']),
});

/**
 * `profile_tax_docs` — panel 14 "Dokumen Pajak".
 *
 * `tax_doc_type` diparse DUA KALI dengan sengaja — `identOf` untuk validator badge kelengkapan DAN
 * predikat baris "Tahun Pajak" (`isSspPair`), `parseObj` untuk tampilan. Pola & alasan yang sama
 * dengan `normalizeDeed`.
 *
 * ⚠️ Normalizer ini SUDAH dipakai `panels/panel-registry.ts` jauh sebelum section "Dokumen Pajak"
 * ada — `TAX_DOCUMENT` bukan `undefinedPanel`. Perubahan di sini wajib **aditif**: menambah baris
 * aman, mengganti/menghapus yang lama mengubah badge kelengkapan di tab "Dokumen Perusahaan"
 * tanpa satu pun error (PLAN-TAX-DOCUMENTS.md D4).
 *
 * `taxYear` tetap `str()` — nilainya hanya DITAMPILKAN, dan mengubahnya ke angka bukan perubahan
 * aditif. `issuedDate` lewat `epochNum()`, BUKAN `str()`: `epochNum()` menerima number, string
 * numerik, maupun string ISO; `str()` membuang nilai number-nya jadi `null`.
 *
 * Response DETAIL (`GET :id`) mengirim objek betulan dan TIDAK boleh dinormalisasi di sini — lihat
 * kepala `sections/tax-documents/models/profile-tax-doc.model.ts`.
 */
export const normalizeTaxDoc = (r: RawEntry): TaxDocEntry => ({
  ...base(r),
  taxDocTypeIdent: identOf(r['tax_doc_type']),
  taxDocType: parseObj<Ref>(r['tax_doc_type']),
  docFile: parseObj<FileRef>(r['doc_file']),
  number: str(r['number']),
  taxYear: str(r['tax_year']),
  issuedDate: epochNum(r['issued_date']),
  expiredDate: epochNum(r['expired_date']),
});

/**
 * `profile_financial_statements` — panel 13 "Laporan Keuangan".
 *
 * Kedua field tanggal lewat `epochNum()`, BUKAN `num()` apalagi `str()`: endpoint LIST panel ini
 * pernah mengirim `"2018-12-31"` (string ISO), yang `num()` ubah jadi `null` tanpa satu pun error.
 *
 * `fiscalYear` memakai `strOrNum()` — `"2018"` di LIST, `2018` di DETAIL — karena nilainya hanya
 * ditampilkan, bukan dihitung.
 *
 * Ke-16 nominal lewat `num()`: BE mengirimnya sebagai STRING desimal (`"242607505.0"`) di LIST dan
 * number di DETAIL, dan nilainya memang untuk DIHITUNG (sembilan total di tabel kartu).
 *
 * ⚠️ Normalizer ini TIDAK didaftarkan di `panels/panel-registry.ts` — `FINANCIAL_STATEMENT` tetap
 * `undefinedPanel`. Mengontrakkannya mengubah badge kelengkapan di tab "Dokumen Perusahaan" tanpa
 * diminta siapa pun (PLAN-FINANCIAL-STATEMENTS.md §1). Preseden persis: `normalizeBankAccount`.
 */
export const normalizeFinancialStatement = (r: RawEntry): FinancialStatementEntry => ({
  ...base(r),
  fiscalYear: strOrNum(r['fiscal_year']),
  financialStatementIssuedDate: epochNum(r['financial_statement_issued_date']),
  financialStatementValidUntil: epochNum(r['financial_statement_valid_until']),
  currency: parseObj<Ref>(r['currency']),
  publicAccountantFullName: str(r['public_accountant_full_name']),
  financialCategory: parseObj<Ref>(r['financial_category']),
  files: parseArr<FileRef>(r['files']),
  cash: num(r['cash']),
  bank: num(r['bank']),
  shortTermInvestment: num(r['short_term_investment']),
  longTermInvestment: num(r['long_term_investment']),
  inventory: num(r['inventory']),
  workInProgress: num(r['work_in_progress']),
  equipmentMachinery: num(r['equipment_machinery']),
  fixedInventory: num(r['fixed_inventory']),
  building: num(r['building']),
  land: num(r['land']),
  otherAsset: num(r['other_asset']),
  accountPayable: num(r['account_payable']),
  taxPayable: num(r['tax_payable']),
  otherPayable: num(r['other_payable']),
  longTermPayable: num(r['long_term_payable']),
  annualRevenue: num(r['annual_revenue']),
});
